import React, { useEffect, useState } from "react";

const API = process.env.REACT_APP_API_URL;
const EMPTY = { deviceId: "", organizationId: "", patientId: "", forename: "", surname: "", birthdate: "" };

export default function DeviceProvisioningModal({ open, organizations, patientId, onClose, onProvisioned }) {
  const [form, setForm] = useState(EMPTY);
  const [devices, setDevices] = useState([]);
  const [patients, setPatients] = useState([]);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setForm({ ...EMPTY, patientId: patientId ? String(patientId) : "" });
    setError("");
    setLoading(true);
    Promise.all(["devices", "patients"].map(async (resource) => {
      const response = await fetch(`${API}/admin/${resource}`, { credentials: "include" });
      if (!response.ok) throw new Error("Could not load enrolled devices and patients.");
      return response.json();
    })).then(([deviceList, patientList]) => {
      if (cancelled) return;
      setDevices(deviceList.filter((device) => device.status === "unclaimed" && !device.patient_id));
      setPatients(patientList.filter((patient) => patient.organization_id && !patient.device));
    }).catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, patientId]);
  if (!open) return null;
  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  async function submit(event) {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const payload = form.patientId ? { patient_id: Number(form.patientId) } : {
        organization_id: Number(form.organizationId),
        patient: { forename: form.forename.trim(), surname: form.surname.trim(), birthdate: form.birthdate },
      };
      const response = await fetch(`${API}/admin/devices/${form.deviceId}/assign`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Device assignment failed.");
      onProvisioned?.(data);
      onClose();
    } catch (err) { setError(err.message); }
    finally { setSubmitting(false); }
  }
  const field = "w-full rounded-lg border border-gray-300 px-3 py-2";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
      <form onSubmit={submit} className="w-full max-w-xl space-y-4 rounded-2xl bg-white p-6 shadow-2xl">
        <h2 className="text-2xl font-semibold">Assign enrolled device</h2>
        <p className="text-sm text-gray-600">Select a device registered during installation. Private owners register by scanning the QR code on the device.</p>
        <label className="block">Device
          <select required className={field} value={form.deviceId} onChange={(e) => update("deviceId", e.target.value)}>
            <option value="">{loading ? "Loading…" : "Select device"}</option>
            {devices.map((device) => <option key={device.id} value={device.id}>{device.public_id}{device.hostname ? ` · ${device.hostname}` : ""}</option>)}
          </select>
        </label>
        {!loading && !devices.length && <p>No unclaimed devices. Run installation on the device first.</p>}
        <label className="block">Patient
          <select className={field} value={form.patientId} onChange={(e) => update("patientId", e.target.value)}>
            <option value="">Create organization patient</option>
            {patients.map((patient) => <option key={patient.id} value={patient.id}>{patient.forename} {patient.surname} · {patient.organization_name}</option>)}
          </select>
        </label>
        {!form.patientId && <>
          <label className="block">Organization<select required className={field} value={form.organizationId} onChange={(e) => update("organizationId", e.target.value)}>
            <option value="">Select organization</option>
            {organizations.map((org) => <option key={org.id} value={org.id}>{org.name}</option>)}
          </select></label>
          {[["forename", "Forename"], ["surname", "Surname"]].map(([key, label]) => <label key={key} className="block">{label}<input required className={field} type="text" value={form[key]} onChange={(e) => update(key, e.target.value)} /></label>)}
          <label className="block">Birthdate<input required className={field} type="date" max={new Date().toISOString().slice(0, 10)} value={form.birthdate} onChange={(e) => update("birthdate", e.target.value)} /></label>
        </>}
        {error && <p role="alert" className="text-red-700">{error}</p>}
        <div className="flex justify-end gap-3">
          <button type="button" disabled={submitting} onClick={onClose} className="rounded-lg border px-4 py-2">Cancel</button>
          <button disabled={loading || submitting || !devices.length} className="rounded-lg bg-blue-500 px-4 py-2 text-white disabled:opacity-60">{submitting ? "Assigning…" : "Assign device"}</button>
        </div>
      </form>
    </div>
  );
}
